import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { Subject, Subscription } from 'rxjs';
import { ConfirmationModalComponent } from '../../../../../_shared/components/modals/confirmation-modal/confirmation-modal.component';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { ComplianceTasksTableComponent } from '../../../_shared/components/compliance-tasks/compliance-tasks-table.component';
import { ComplianceStateService } from '../../../_shared/services/compliance-state.service';
import { ComplianceOverviewService } from '../../services/compliance-overview.service';
import { EditPeriodicComplianceTaskModalComponent } from '../modals/edit-periodic-compliance-task-modal/edit-periodic-compliance-task-modal.component';
import {
  RescheduleComplianceTaskModalComponent,
  RescheduleComplianceTaskModalRequest
} from '../../../_shared/components/modals/reschedule-compliance-task-modal/reschedule-compliance-task-modal.component';
import {
  ComplianceStatus,
  ComplianceStatusFilter,
  PeriodicComplianceTask,
  PeriodicComplianceTaskRow,
  PeriodicComplianceTasksRequest,
  TableColumn
} from '../../models/compliance-item.model';
import { startOfDay } from 'date-fns';
import { TranslocoService } from '@jsverse/transloco';
import { translateComplianceFrequency } from '../../../_shared/helpers/compliance-lookup-label.helper';


@Component({
  selector: 'app-periodic-compliance-tasks',
  standalone: true,
  imports: [
    ComplianceTasksTableComponent
  ],
  templateUrl: './periodic-compliance-tasks.component.html',
  styleUrl: './periodic-compliance-tasks.component.scss'
})
export class PeriodicComplianceTasksComponent implements OnInit, OnDestroy {
  private complianceOverviewService: ComplianceOverviewService = inject(ComplianceOverviewService);
  private complianceOverviewStateService: ComplianceStateService = inject(ComplianceStateService);
  private errorService: ErrorHandlerService = inject(ErrorHandlerService);
  private router: Router = inject(Router);
  private modalService: NgbModal = inject(NgbModal);
  private toastr: ToastrService = inject(ToastrService);
  private transloco: TranslocoService = inject(TranslocoService);
  private requestSubscription?: Subscription;
  private readonly cancelSubject = new Subject<void>();
  private accountIds: number[] = [];
  private regionId: number | null = null;
  private countryId: number | null = null;
  private complianceId: number | null = null;
  private searchTerm: string | null = null;
  private selectedStatus: ComplianceStatusFilter = ComplianceStatusFilter.All;

  periodicComplianceTasks: PeriodicComplianceTaskRow[] = [];
  isLoading = true;
  isLoadingMore = false;
  hasError = false;
  currentPage = 1;
  pageSize = 20;
  totalPages = 0;
  totalCount = 0;

  columns: TableColumn[] = [
    { header: this.transloco.translate('compliance.overview.periodicTasks.columns.asset'), value: 'assetName', width: 'col-2', type: 'text', truncate: true, title: true, bold: true },
    { header: this.transloco.translate('compliance.overview.periodicTasks.columns.frequency'), value: 'frequencyLabel', width: 'col', type: 'text' },
    { header: this.transloco.translate('compliance.overview.periodicTasks.columns.complianceStandard'), value: 'complianceStandard', width: 'col', type: 'text', truncate: true, title: true },
    { header: this.transloco.translate('compliance.overview.periodicTasks.columns.workshop'), value: 'workshopName', width: 'col', type: 'text' },
    { header: this.transloco.translate('compliance.overview.periodicTasks.columns.dueDate'), value: 'dueDateLabel', width: 'col', type: 'text' },
    { header: this.transloco.translate('compliance.overview.periodicTasks.columns.taskStatus'), value: 'complianceStatusLabel', width: 'col', type: 'status' },
    { header: this.transloco.translate('compliance.overview.periodicTasks.columns.action'), value: 'action', width: 'col-1', type: 'action' },
  ];

  ngOnInit(): void {
    this.requestSubscription = this.complianceOverviewStateService.request$.subscribe(request => {
      this.accountIds = request.accountIds ?? [];
      this.regionId = request.complianceRegion?.type === 'region' ? request.complianceRegion.id : null;
      this.countryId = request.complianceRegion?.type === 'country' ? request.complianceRegion.id : null;
      this.complianceId = request.complianceId ?? null;

      this.resetAndLoad();
    });
  }

  ngOnDestroy(): void {
    this.requestSubscription?.unsubscribe();
    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  async getPeriodicComplianceTasksAsync(resetResults: boolean = false): Promise<void> {
    this.hasError = false;
    const requestedPage = this.currentPage;
    const request = this.buildRequest(requestedPage);

    try {
      if (requestedPage === 1 || resetResults) {
        this.isLoading = true;
        this.isLoadingMore = false;
        this.cancelSubject.next();
      } else {
        this.isLoadingMore = true;
      }

      const results = await this.complianceOverviewService.getPeriodicComplianceTasksAsync(
        request,
        this.cancelSubject
      );
      const pagination = results.data;
      const periodicComplianceTasks = (pagination?.results ?? []).map(item => this.mapPeriodicComplianceTask(item));

      if (requestedPage === 1) {
        this.periodicComplianceTasks = periodicComplianceTasks;
      } else {
        this.periodicComplianceTasks = [...this.periodicComplianceTasks, ...periodicComplianceTasks];
      }

      this.totalCount = pagination?.totalCount ?? 0;
      this.totalPages = pagination?.totalPages ?? 0;

      this.isLoading = false;
      this.isLoadingMore = false;
    } catch (error: any) {
      this.errorService.handleError(error, {
        callback: () => {
          this.hasError = true;
          this.isLoading = false;
          this.isLoadingMore = false;
        }
      });
    }
  }

  onRowClick(item: PeriodicComplianceTaskRow): void {
    this.router.navigate(['/asset-management/compliance-overview/asset-periodic-compliance-details'], {
      queryParams: {
        id: item.id,
        assetId: item.assetId,
        complianceId: item.complianceId,
        assetName: item.assetName
      }
    });
  }

  onEditClick(item: PeriodicComplianceTaskRow): void {
    const modalRef = this.modalService.open(EditPeriodicComplianceTaskModalComponent, {
      centered: true,
      backdrop: 'static'
    });
    modalRef.componentInstance.accountId = item.accountId;
    modalRef.componentInstance.task = item;
    modalRef.closed.subscribe((shouldReload: boolean) => {
      if (shouldReload) {
        this.resetAndLoad();
      }
    });
  }

  onRescheduleClick(item: PeriodicComplianceTaskRow): void {
    const modalRef = this.modalService.open(RescheduleComplianceTaskModalComponent, {
      centered: true,
      backdrop: 'static'
    });
    modalRef.componentInstance.initialDate = item.dueDate;
    modalRef.componentInstance.showNotifyPersonResponsible = true;
    modalRef.componentInstance.successMessage = this.transloco.translate('compliance.overview.periodicTasks.toasts.rescheduled');
    modalRef.componentInstance.submitHandler = async (request: RescheduleComplianceTaskModalRequest) => {
      await this.complianceOverviewService.reschedulePeriodicComplianceTaskAsync({
        id: item.id,
        assetId: item.assetId,
        dueDate: request.dueDate,
        notifyPersonResponsible: request.notifyPersonResponsible
      });
    };
    modalRef.closed.subscribe((shouldReload: boolean) => {
      if (shouldReload) {
        this.resetAndLoad();
      }
    });
  }

  onDeleteClick(item: PeriodicComplianceTaskRow): void {
    const modalRef = this.modalService.open(ConfirmationModalComponent, {
      centered: true,
      keyboard: false,
      backdrop: 'static',
      windowClass: 'confirmation-modal__content'
    });

    modalRef.componentInstance.data = {
      title: this.transloco.translate('compliance.overview.periodicTasks.confirmations.delete.title'),
      message: this.transloco.translate('compliance.overview.periodicTasks.confirmations.delete.message', { asset: item.assetName }),
      warningMessage: this.transloco.translate('compliance.overview.periodicTasks.confirmations.delete.warning'),
      confirmationPrompt: 'delete',
      confirmPlaceholder: this.transloco.translate('compliance.overview.periodicTasks.confirmations.delete.placeholder'),
      confirmText: this.transloco.translate('compliance.overview.periodicTasks.confirmations.delete.confirm'),
      declineText: this.transloco.translate('compliance.overview.periodicTasks.confirmations.delete.decline'),
      closeOnSuccess: false,
      onConfirm: async () => {
        try {
          await this.complianceOverviewService.deletePeriodicComplianceTaskAsync({
            id: item.id,
            assetId: item.assetId
          });
          this.toastr.success(this.transloco.translate('compliance.overview.periodicTasks.toasts.deleted'));
          modalRef.close(true);
        } catch (error: any) {
          this.errorService.handleError(error, {
            showToast: true
          });
        }
      }
    };

    modalRef.closed.subscribe((shouldReload: boolean) => {
      if (shouldReload) {
        this.resetAndLoad();
      }
    });
  }

  onSearch(term: string): void {
    this.searchTerm = term?.trim() || null;
    this.resetAndLoad();
  }

  onStatusFilter(status: ComplianceStatusFilter): void {
    this.selectedStatus = status;
    this.resetAndLoad();
  }

  onScrollDown(): void {
    if (this.isLoading || this.isLoadingMore || this.currentPage >= this.totalPages) {
      return;
    }

    this.currentPage++;
    void this.getPeriodicComplianceTasksAsync();
  }

  private resetAndLoad(): void {
    this.currentPage = 1;
    this.totalPages = 0;
    this.totalCount = 0;
    this.periodicComplianceTasks = [];
    void this.getPeriodicComplianceTasksAsync(true);
  }

  private buildRequest(page: number): PeriodicComplianceTasksRequest {
    return {
      accountIds: this.accountIds,
      page,
      pageSize: this.pageSize,
      regionId: this.regionId,
      countryId: this.countryId,
      complianceId: this.complianceId,
      complianceStatus: this.selectedStatus === ComplianceStatusFilter.All
        ? null
        : this.selectedStatus as unknown as ComplianceStatus,
      searchTerm: this.searchTerm
    };
  }

  private mapPeriodicComplianceTask(item: PeriodicComplianceTask): PeriodicComplianceTaskRow {
    const dueDate = new Date(item.dueDate);

    return {
      ...item,
      dueDate: startOfDay(dueDate),
      frequencyLabel: translateComplianceFrequency(this.transloco, item.frequency),
      dueDateLabel: this.formatDate(dueDate),
      complianceStatusLabel: this.getComplianceStatusLabel(item.complianceStatus),
      accountId: item.accountId
    };
  }

  private getComplianceStatusLabel(status: ComplianceStatus): string {
    const labels: Record<ComplianceStatus, string> = {
      [ComplianceStatus.Pending]: 'Pending',
      [ComplianceStatus.InProgress]: 'In-Progress',
      [ComplianceStatus.Completed]: 'Completed',
      [ComplianceStatus.Overdue]: 'Overdue',
      [ComplianceStatus.Missed]: 'Missed'
    };

    return labels[status];
  }

  private formatDate(date: Date): string {
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    return new Intl.DateTimeFormat('en-ZA', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(new Date(date));
  }
}